/*
  Stages tab in read mode + the stage drawer (UI overhaul wave 2, screen 04 ideas 4 and 10). Pure helpers.

  isBlankStageLine(line)          → the empty placeholder item the page keeps in every stage
  summarizeStageItems(lines)      → { count, names, references, subtitle } for one stage row
  cloneStageLines(lines)          → deep copy for the drawer's draft
  countStageDraftChanges(a, b)    → how many fields / items differ (the drawer's "N unsaved changes")
  linesForSave(lines)             → the draft without blank placeholder items
  stageRowState(stage, nextId)    → 'done' | 'current' | 'todo'
*/
import { toDateInputValue } from '@/lib/format';
import { isStageDone } from './projectDetailModel';

const text = (value) => String(value ?? '').trim();

const LINE_FIELDS = ['name', 'date', 'note', 'status', 'reference'];
const DEFAULT_LINE_STATUS = 'In Progress';

/** An item nobody filled in (the page keeps one empty item per stage as a placeholder). */
export function isBlankStageLine(line) {
  if (!line || typeof line !== 'object') return true;
  if (['name', 'date', 'note', 'reference'].some((field) => text(line[field]))) return false;
  if (text(line.status) && text(line.status) !== DEFAULT_LINE_STATUS) return false;
  if ((line.extra_fields || []).some((field) => text(field?.title) || text(field?.content))) return false;
  if ((line.sub_items || []).some((subItem) => !isBlankStageLine(subItem))) return false;
  return true;
}

/** Items of one stage, as the read-mode row shows them. */
export function summarizeStageItems(lines = []) {
  const items = (lines || []).filter((line) => !isBlankStageLine(line));
  const names = items.map((line) => text(line.name)).filter(Boolean);
  const references = [];
  items.forEach((line) => {
    if (text(line.reference)) references.push(text(line.reference));
    (line.sub_items || []).forEach((subItem) => { if (text(subItem?.reference)) references.push(text(subItem.reference)); });
  });
  const subItemCount = items.reduce((sum, line) => sum + (line.sub_items || []).filter((subItem) => !isBlankStageLine(subItem)).length, 0);
  let subtitle = 'No items yet';
  if (items.length) {
    const head = `${items.length} item${items.length === 1 ? '' : 's'}`;
    if (names.length) subtitle = `${head} · ${names.slice(0, 3).join(' · ')}${names.length > 3 ? ` · +${names.length - 3} more` : ''}`;
    else if (references.length) subtitle = `${head} · reference attached`;
    else subtitle = head;
  }
  return { count: items.length, items, names, references, subItemCount, subtitle };
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** A new, empty stage item (same shape as the page's createStageLine). */
export const newStageLine = (overrides = {}) => ({
  id: newId(),
  date: '',
  name: '',
  note: '',
  status: DEFAULT_LINE_STATUS,
  reference: '',
  extra_fields: [],
  sub_items: [],
  ...overrides,
});

export const newStageExtraField = () => ({ id: newId(), title: '', content: '' });

/** A copy of an item with new ids (Duplicate item). */
export function duplicateStageLine(line, { isSubItem = false, rename = true } = {}) {
  return newStageLine({
    ...line,
    id: newId(),
    name: rename && text(line?.name) ? `${text(line.name)} (copy)` : (line?.name || ''),
    extra_fields: (line?.extra_fields || []).map((field) => ({ id: newId(), title: field.title || '', content: field.content || '' })),
    sub_items: isSubItem ? [] : (line?.sub_items || []).map((subItem) => duplicateStageLine(subItem, { isSubItem: true, rename: false })),
  });
}

const cloneLine = (line) => ({
  ...line,
  extra_fields: (line?.extra_fields || []).map((field) => ({ ...field })),
  sub_items: (line?.sub_items || []).map((subItem) => cloneLine(subItem)),
});

export const cloneStageLines = (lines = []) => (lines || []).map(cloneLine);

const fieldValue = (line, field) => (field === 'status' ? (text(line?.[field]) || DEFAULT_LINE_STATUS) : text(line?.[field]));

function countLineChanges(before, after) {
  let changes = 0;
  LINE_FIELDS.forEach((field) => {
    if (fieldValue(before, field) !== fieldValue(after, field)) changes += 1;
  });
  const beforeFields = new Map((before?.extra_fields || []).map((field) => [field.id, field]));
  const afterFields = (after?.extra_fields || []);
  afterFields.forEach((field) => {
    const original = beforeFields.get(field.id);
    if (!original) {
      if (text(field.title) || text(field.content)) changes += 1;
      return;
    }
    if (text(original.title) !== text(field.title) || text(original.content) !== text(field.content)) changes += 1;
  });
  const afterFieldIds = new Set(afterFields.map((field) => field.id));
  (before?.extra_fields || []).forEach((field) => {
    if (!afterFieldIds.has(field.id) && (text(field.title) || text(field.content))) changes += 1;
  });
  changes += countListChanges(before?.sub_items || [], after?.sub_items || []);
  return changes;
}

function countListChanges(beforeLines = [], afterLines = []) {
  let changes = 0;
  const beforeById = new Map(beforeLines.map((line) => [line.id, line]));
  const afterIds = new Set(afterLines.map((line) => line.id));
  afterLines.forEach((line) => {
    const original = beforeById.get(line.id);
    if (!original) {
      if (!isBlankStageLine(line)) changes += 1;
      return;
    }
    changes += countLineChanges(original, line);
  });
  beforeLines.forEach((line) => {
    if (!afterIds.has(line.id) && !isBlankStageLine(line)) changes += 1;
  });
  return changes;
}

/** Number of changed fields, added and removed items between the saved stage and the drawer's draft. */
export function countStageDraftChanges({ status: beforeStatus, lines: beforeLines } = {}, { status: afterStatus, lines: afterLines } = {}) {
  let changes = text(beforeStatus) !== text(afterStatus) ? 1 : 0;
  changes += countListChanges(beforeLines || [], afterLines || []);
  return changes;
}

/** What the drawer saves: blank placeholder items (and blank sub-items / extra fields) are left out. */
export function linesForSave(lines = []) {
  return (lines || [])
    .filter((line) => !isBlankStageLine(line))
    .map((line) => ({
      ...line,
      extra_fields: (line.extra_fields || []).filter((field) => text(field?.title) || text(field?.content)),
      sub_items: (line.sub_items || []).filter((subItem) => !isBlankStageLine(subItem)).map((subItem) => ({
        ...subItem,
        extra_fields: (subItem.extra_fields || []).filter((field) => text(field?.title) || text(field?.content)),
        sub_items: [],
      })),
    }));
}

/** Row state in the read-mode list: approved, the next stage to work on, or later. */
export function stageRowState(stage, nextStageId) {
  if (isStageDone(stage?.status)) return 'done';
  if (stage?.id && stage.id === nextStageId) return 'current';
  return 'todo';
}

/* ---------------------------------------------------------------- wave 3 (screen 04 idea 8): stage clocks */

const DONE_STATUSES = new Set(['Approved', 'Completed']);
const OPEN_STATUSES = new Set(['In Progress', 'Submitted', 'Rejected', 'Bottlenecked']);
const DAY_MS = 86400000;
const stageStatus = (stage) => text(stage?.status) || 'Not Started';

/** The calendar day of a date / ISO instant in the company time zone (Admin › Settings; GST by default), as a day
 *  number (UTC days since epoch). A plain 'YYYY-MM-DD' is that day as written. */
function companyDay(value) {
  const raw = text(value);
  if (!raw) return null;
  const iso = toDateInputValue(raw);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);
}

/**
 * How long a stage has been where it is (mirrors backend/stage_sla.py stage_clock):
 * - an open stage (In Progress, Submitted, Rejected, Bottlenecked): days since its status last changed
 *   (`status_since`, stamped by the server on every status change);
 * - the next stage that has not started: days since the stage before it was approved ("Waiting 2 d");
 * - approved stages and later stages that have not started: no clock (null). No recorded date → null.
 * → { kind: 'status' | 'waiting', status, since, days, slaDays, overdue } | null
 */
export function stageClock(stages = [], index = 0, { now } = {}) {
  const list = (stages || []).filter(Boolean);
  const stage = list[index];
  if (!stage) return null;
  const status = stageStatus(stage);
  if (DONE_STATUSES.has(status)) return null;
  let kind = null;
  let since = null;
  if (OPEN_STATUSES.has(status)) {
    kind = 'status';
    since = text(stage.status_since) || null;
  } else {
    const firstOpen = list.findIndex((entry) => !DONE_STATUSES.has(stageStatus(entry)));
    if (firstOpen !== index) return null;
    const candidates = [text(stage.status_since)];
    if (index > 0 && DONE_STATUSES.has(stageStatus(list[index - 1]))) candidates.push(text(list[index - 1].status_since));
    const dated = candidates.filter((value) => companyDay(value) !== null);
    if (dated.length) {
      kind = 'waiting';
      since = dated.reduce((latest, value) => (companyDay(value) > companyDay(latest) ? value : latest));
    }
  }
  const sinceDay = companyDay(since);
  if (!kind || sinceDay === null) return null;
  const today = companyDay(now ? new Date(now).toISOString() : new Date().toISOString());
  const days = Math.max(0, today - sinceDay);
  const slaDays = Math.max(0, Math.round(Number(stage.sla_days) || 0));
  return { kind, status, since, days, slaDays, overdue: Boolean(slaDays && days > slaDays) };
}

/** { [stageId]: clock } for the stages that have one. */
export function stageClocks(stages = [], options = {}) {
  const result = {};
  (stages || []).forEach((stage, index) => {
    const clock = stageClock(stages, index, options);
    if (clock && stage?.id) result[stage.id] = clock;
  });
  return result;
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Words for a clock: `chip` for a stage row next to its status pill ("Waiting 2 d" / "20 d · SLA 14 d"),
 * `compact` for the narrow pipeline steps ("Waiting 2 d" / "20 d · late") and a sentence for tooltips and screen
 * readers ("In Technical Submittal (Submitted) for 20 days — over its 14-day SLA").
 */
export function describeStageClock(clock, stageName = 'this stage') {
  if (!clock) return null;
  const days = `${clock.days} d`;
  const short = clock.kind === 'waiting' ? `Waiting ${days}` : days;
  const sla = clock.slaDays ? `SLA ${clock.slaDays} d` : '';
  const sentence = clock.kind === 'waiting'
    ? `${stageName} has been waiting to start for ${plural(clock.days, 'day')}`
    : `In ${stageName} (${clock.status}) for ${plural(clock.days, 'day')}`;
  const slaSentence = clock.slaDays
    ? (clock.overdue ? ` — over its ${clock.slaDays}-day SLA` : ` — SLA ${clock.slaDays} days`)
    : '';
  return {
    short,
    chip: sla && (clock.overdue || clock.kind === 'status') ? `${short} · ${sla}` : short,
    // the pipeline's narrow column: a late wait drops "Waiting" (its step already says "Next · Not started")
    compact: clock.overdue ? `${clock.kind === 'waiting' ? days : short} · late` : short,
    sentence: `${sentence}${slaSentence}`,
    tone: clock.overdue ? 'red' : clock.slaDays && clock.days >= clock.slaDays * 0.75 ? 'amber' : clock.kind === 'waiting' ? 'amber' : 'gray',
  };
}
